import {
  REQUIRED_MESSAGE,
  dimensionProblem,
  text,
  urlProblem,
  when,
} from "@/lib/catalog-item-form-rules";

// The Add Variant dialog's model: what the typist has entered, what is wrong
// with it, and what that becomes on the wire.
//
// A sibling of `@/lib/request-item-form` rather than a configuration of it, and
// that is the donor's arrangement: `useVariantForm.ts` is a separate form with
// its own defaults, its own rules and its own `buildPostData`, and it disagrees
// with the request form on three things that only show up field by field —
//
//   1. **no Prop ID control at all**, so nothing to validate and nothing to send;
//   2. **no cap on Model Number**, where Request Item passes `maxLength: 50`;
//   3. **Manufacturer and Category are inherited and locked**, so they are not
//      draft state — they belong to the parent item, and Python derives them
//      from the path rather than reading them off the body (ADR-0005 decision
//      13). The wire face has no field for either.
//
// What the two forms *do* share is in `@/lib/catalog-item-form-rules`, because
// the donor shares it too.
//
// **`lib/`, not beside the dialog**, for the same reason the request form is:
// these are decisions the dialog only displays, and the ticket asks for them
// under a unit test.

/**
 * The dialog's fields, one per control — the locked pair excluded.
 *
 * The two selects hold an id or `null`; every text field holds a string, blank
 * when untouched. `description` is the rich-text field's HTML.
 */
export interface AddVariantDraft {
  name: string;
  finishId: number | null;
  materialId: number | null;
  modelNumber: string;
  jdeItemNumber: string;
  dimensionWidth: string;
  dimensionHeight: string;
  dimensionDepth: string;
  url: string;
  description: string;
}

/**
 * What the locked fields are read off — the item the dialog was opened from.
 *
 * A structural subset, so a `CatalogDetailItem` satisfies it without a cast. It
 * is here rather than in the component because of the *category* rule below: the
 * parent is part of what makes a draft submittable, not merely part of what the
 * dialog displays.
 */
export interface AddVariantParent {
  category?: { name: string } | null;
}

/**
 * `categoryId` is a problem key without being a draft key.
 *
 * The donor's Category select is `required` *and* `disabled`, so its refusal
 * lands on a control the typist cannot reach — see `addVariantFieldProblems`.
 * Naming it here is what lets the dialog show that message in the right place.
 */
export type AddVariantFieldProblems = Partial<Record<keyof AddVariantDraft | "categoryId", string>>;

export const blankAddVariantDraft = (): AddVariantDraft => ({
  name: "",
  finishId: null,
  materialId: null,
  modelNumber: "",
  jdeItemNumber: "",
  dimensionWidth: "",
  dimensionHeight: "",
  dimensionDepth: "",
  url: "",
  description: "",
});

/**
 * Every problem the browser can see, keyed by field. An empty object is a
 * submittable draft.
 *
 * **The category rule is the donor's dead end, ported.** `useVariantForm.ts`
 * fills the locked Category select from the parent — `id: category?.id || ''` —
 * and `CategoriesCustomSelect required` then refuses the empty string, so a
 * variant of an *uncategorised* item is refused with "This field is required"
 * under a select the typist cannot change. The only way out in modsy is to give
 * the parent a category first.
 *
 * Carried rather than quietly dropped: the source's observable behavior is the
 * spec, and this one is reachable — the Add Variant menu entry is gated on the
 * Unspecified manufacturer only. It is on #254's decision menu as a candidate
 * donor bug, which is where a divergence gets ruled rather than assumed.
 */
export function addVariantFieldProblems(
  draft: AddVariantDraft,
  parent: AddVariantParent,
): AddVariantFieldProblems {
  const problems: AddVariantFieldProblems = {};

  // Bare `required`, and deliberately **not** `!draft.name.trim()`: the donor's
  // rule is react-hook-form's `required: 'This field is required'`
  // (`AddVariantIsland.tsx:107`), which counts `""` as missing and lets `"   "`
  // through, and `useVariantForm.ts:97` then posts `data.name` untrimmed. So a
  // variant can be named three spaces in modsy, and refusing one here would be
  // a parity break — a better-looking one, which the guidelines rule out the
  // same way. Raised on #254's decision menu, alongside the fact that the
  // Request Item form (whose donor rule is identical, `RequestItemIsland.tsx:100`)
  // does trim.
  if (!draft.name) problems.name = REQUIRED_MESSAGE;
  if (draft.finishId === null) problems.finishId = REQUIRED_MESSAGE;
  if (!parent.category) problems.categoryId = REQUIRED_MESSAGE;

  const url = urlProblem(draft.url);
  if (url) problems.url = url;

  for (const field of ["dimensionWidth", "dimensionHeight", "dimensionDepth"] as const) {
    const problem = dimensionProblem(draft[field]);
    if (problem) problems[field] = problem;
  }

  return problems;
}

/**
 * The variant as the add-variant server function takes it.
 *
 * **Declared here, not imported from `@/api/contracts/catalog`** — `lib/` sits
 * below both graphs and may not import upward (`client/docs/architecture.md`,
 * "The dependency rule"). The two shapes are held together by a drift test in
 * the `api/` tier, exactly as the request form's payload is.
 *
 * `itemId` is the **parent's**, and it rides the path rather than the body.
 */
export interface AddVariantPayload {
  itemId: number;
  name: string;
  finishId: number;
  materialId?: number;
  modelNumber?: string;
  jdeItemNumber?: string;
  url?: string;
  description?: string;
  dimensionWidth?: string;
  dimensionHeight?: string;
  dimensionDepth?: string;
}

/**
 * The draft as the add-variant input — the donor's `onSubmit` payload build.
 *
 * `variantGroupId` is absent on purpose, and it is the one field the donor sends
 * that Atlas does not: modsy computes `item?.variantGroupId || activeItemId` in
 * the browser and posts it, while Python derives the group from the parent the
 * path names. Sending it would be the browser deciding which group a variant
 * joins.
 */
export function addVariantInputFromDraft(
  itemId: number,
  draft: AddVariantDraft,
): AddVariantPayload {
  if (draft.finishId === null) {
    // Unreachable from the dialog, which cannot submit with a problem
    // outstanding. It states the rule rather than casting it away, so a future
    // caller that skips the check is told why.
    throw new Error("A variant needs a color/finish.");
  }

  return {
    itemId,
    // Untrimmed — `useVariantForm.ts:97` posts `data.name` as typed. See the
    // name rule above.
    name: draft.name,
    finishId: draft.finishId,
    ...(draft.materialId === null ? {} : { materialId: draft.materialId }),
    ...when("modelNumber", text(draft.modelNumber)),
    ...when("jdeItemNumber", text(draft.jdeItemNumber)),
    ...when("dimensionWidth", text(draft.dimensionWidth)),
    ...when("dimensionHeight", text(draft.dimensionHeight)),
    ...when("dimensionDepth", text(draft.dimensionDepth)),
    ...when("url", text(draft.url)),
    ...when("description", text(draft.description)),
  };
}
